import { useQuery } from '@apollo/client';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, Outlet, useOutletContext } from 'react-router';
import { graphql } from '~/apollo/generated/v3';
import type { UploadRegionUpdatePageQuery } from '~/apollo/generated/v3/graphql';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { NavTabs } from '~/components/navigation/NavTabs';
import { useRouteParam } from '~/hooks/routing';
import {
  regionRoute,
  uploadRegionUpdateGeoreferencesRoute,
  uploadRegionUpdateOutcropsRoute,
  uploadRegionUpdateRoute,
  uploadRegionUpdateStudiesRoute,
} from '~/paths';

const UPLOAD_REGION_UPDATE_PAGE = graphql(`
  query UploadRegionUpdatePage($regionId: Int!) {
    regionList(id: $regionId) {
      ...regionParts
    }
  }
`);

type UploadRegionUpdatePageOutletContext = {
  region: UploadRegionUpdatePageQuery['regionList'][number];
};

export default function UploadRegionUpdatePage() {
  const regionId = useRouteParam('regionId', parseInt);

  const { data, loading } = useQuery(UPLOAD_REGION_UPDATE_PAGE, {
    variables: { regionId },
  });

  const regionList = data?.regionList ?? [];
  const region = regionList.find(r => r.id === regionId);

  useBreadcrumb(
    'routes/upload/model/region/$regionId',
    region?.name ?? 'Update Region',
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!region) return <NotFound />;

  const outletContext: UploadRegionUpdatePageOutletContext = { region };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-end gap-4">
        <PageHeading>{region.name}</PageHeading>

        <Link
          to={regionRoute(regionId)}
          target="_blank"
          className="float-right btn btn-sm btn-primary gap-1"
        >
          View Region <FontAwesomeIcon icon={faExternalLink} />
        </Link>
      </div>

      <NavTabs>
        <NavTabs.Tab to={uploadRegionUpdateRoute(region.id)} end>
          Region
        </NavTabs.Tab>
        <NavTabs.Tab to={uploadRegionUpdateGeoreferencesRoute(region.id)}>
          Georeferences
        </NavTabs.Tab>
        <NavTabs.Tab to={uploadRegionUpdateOutcropsRoute(region.id)}>
          Outcrops
        </NavTabs.Tab>
        <NavTabs.Tab to={uploadRegionUpdateStudiesRoute(region.id)}>
          Studies
        </NavTabs.Tab>
      </NavTabs>

      <Outlet context={outletContext} />
    </div>
  );
}

export function useUploadRegionUpdatePageContext() {
  return useOutletContext<UploadRegionUpdatePageOutletContext>();
}
